import React, { useState, useEffect } from 'react';
import { UserData, AppSettings } from '../types';
import AdSpace from '../components/AdSpace';
import { useSetMeta } from '../hooks/useSetMeta';
import { calculateAge } from '../utils/calculator';
import { TAROT_DECK } from '../constants';
import { Star, Milestone, Sparkles, ChevronDown, Loader2, Clock, ArrowRight } from 'lucide-react';
import { generateAIContent } from '../services/ai';
import { getTimeUntilAvailable } from '../utils/cosmic';
import timelineData from '../src/content/timeline.json';

interface TimelinePageProps {
  userData: UserData;
  settings: AppSettings;
  updateSettings: (newSettings: Partial<AppSettings>) => void;
}
const MILESTONE_DATA = {
  0: {
    label: "The Beginning",
    description: "Life's entry point and the formation of core identity.",
    explanation: "This marks the start of your journey. It's a foundational period where your basic temperament and responses to the world begin to form.",
    themes: ["Identity Formation", "Initial Environment", "First Connections"]
  },
  7: {
    label: "Saturn Square",
    description: "First exposure to structure, discipline, and boundaries.",
    explanation: "This phase often introduces the concept of rules and responsibilities. It's a time of learning how personal desires meet external limits, shaping your understanding of effort and consequence.",
    themes: ["Developing Discipline", "Understanding Rules", "First Sense of Responsibility"]
  },
  12: {
    label: "Jupiter Return",
    description: "A time of learning, optimism, and expanding horizons.",
    explanation: "Marked by a broadening of perspective, this phase encourages exploration and learning. Confidence grows as you start to form a wider understanding of the world and your place in it.",
    themes: ["Personal Growth", "Expanding Beliefs", "Developing Optimism"]
  },
  18: {
    label: "Nodal Return",
    description: "A key point for discovering independence and life direction.",
    explanation: "This is a significant turning point related to your life path. It often brings a stronger sense of purpose, major life decisions, and a move towards greater independence.",
    themes: ["Finding Direction", "Increased Independence", "Fated Encounters"]
  },
  21: {
    label: "Saturn Square II",
    description: "A test of maturity and re-evaluation of your structures.",
    explanation: "This phase challenges the structures you've built so far. It's a time to assess your choices, take accountability, and make adjustments to your path based on your experiences.",
    themes: ["Maturing Responsibility", "Re-evaluating Goals", "Building solid foundations"]
  },
  30: {
    label: "Saturn Return",
    description: "A major life chapter of maturation and commitment.",
    explanation: "Often considered one of life's most significant rites of passage, the Saturn Return is a period of profound maturation. It calls for you to step into a new level of adulthood, take full responsibility for your life, and make long-term commitments.",
    themes: ["True Adulthood", "Major Life Decisions", "Career and Relationship Commitments"]
  },
  42: {
    label: "Uranus Opposition",
    description: "A mid-life call for authenticity and personal freedom.",
    explanation: "This phase often brings a desire to break free from routine and express your true self. It can be a time of unexpected changes, renewed creativity, and a quest for personal liberation.",
    themes: ["Mid-life Awakening", "Seeking Authenticity", "Breaking Old Patterns"]
  },
  50: {
    label: "Chiron Return",
    description: "An opportunity for deep healing and sharing wisdom.",
    explanation: "This period brings your core wounds and vulnerabilities to the surface, not to cause pain, but to offer a chance for deep healing. It's a time when you can transform past struggles into wisdom that you can share with others.",
    themes: ["Healing Core Wounds", "Embracing Vulnerability", "Teaching and Mentorship"]
  },
};
const TimelineItem: React.FC<{ 
    milestone: any, 
    isCurrentEra: boolean, 
    isPast: boolean, 
    isExpanded: boolean, 
    onToggle: () => void, 
    userData: UserData,
    cooldownStatus: { locked: boolean, text: string },
    onCooldownStart: () => void
}> = ({ milestone, isCurrentEra, isPast, isExpanded, onToggle, userData, cooldownStatus, onCooldownStart }) => {
    const [loading, setLoading] = useState(false);
    const [insight, setInsight] = useState<string | null>(null);
    const handleExplore = async (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent toggling the accordion
        if (insight) return; // Already loaded
        if (cooldownStatus.locked) return; // Cooldown active
        setLoading(true);
        onCooldownStart(); // Start cooldown
        try {
            const prompt = `
                Generate a short, personalized astrological insight (max 100 words) for the life milestone "${milestone.label}" (Age ${milestone.age}).
                User details:
                - Birth Date: ${userData.birthDate}
                - Birth Place: ${userData.birthPlace}
                
                Explain how this milestone specifically relates to their astrological profile if possible, or give general wisdom for this phase.
                Tone: Mystical, encouraging, and insightful.
            `;
            const responseText = await generateAIContent(prompt);
            
            if (responseText) {
                setInsight(responseText);
            }
        } catch (error) {
            console.error("Error generating insight:", error);
            setInsight("The stars are clouded at the moment. Please try again later.");
        } finally {
            setLoading(false);
        }
    };
    return (
        <div className={`relative pl-8 md:pl-12 transition-all duration-500 ${isPast ? 'opacity-100' : 'opacity-50 grayscale'}`}>
            {/* Dot */}
            <div className={`
                absolute -left-[9px] top-1 w-4 h-4 rounded-full border-4 border-[#020410] 
                ${isCurrentEra ? 'bg-secondary scale-125 shadow-[0_0_15px_#A855F7]' : 'bg-gray-700'}
            `}></div>
            
            <div 
                className="bg-card/30 border border-gray-800 rounded-xl p-5 hover:bg-card/50 transition-colors focus:outline-none focus:ring-2 focus:ring-secondary"
                onClick={onToggle}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onToggle()}
                tabIndex={0}
                role="button"
                aria-expanded={isExpanded}
                aria-controls={`milestone-content-${milestone.age}`}
            >
                <div className="flex justify-between items-start mb-2">
                    <div>
                        <h4 className={`text-lg font-bold ${isCurrentEra ? 'text-secondary' : 'text-white'}`}>
                            {milestone.label}
                        </h4>
                        <p className="text-gray-400 text-sm mt-1">{milestone.description}</p>
                    </div>
                    <span className="text-2xl font-display font-bold text-gray-600">{milestone.year}</span>
                </div>
                <p className="text-gray-400 text-sm flex items-center gap-2">
                    Age {milestone.age} 
                    {isCurrentEra && <span className="text-xs bg-secondary text-white px-2 py-0.5 rounded-full">Current Phase</span>}
                </p>
                {isExpanded && (
                    <div id={`milestone-content-${milestone.age}`} className="mt-6 animate-in fade-in zoom-in-95 cursor-default" onClick={(e) => e.stopPropagation()}>
                        <p className="text-gray-300 leading-relaxed">{milestone.explanation}</p>
                        <ul className="list-disc list-inside space-y-2 mt-4 text-gray-400">
                            {milestone.themes.map((theme: string, i: number) => <li key={i}>{theme}</li>)}
                        </ul>
                        
                        {insight && (
                            <div className="mt-6 p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg animate-in fade-in slide-in-from-bottom-2">
                                <div className="flex items-center gap-2 mb-2 text-purple-300">
                                    <Sparkles size={16} />
                                    <span className="font-bold text-sm uppercase tracking-wider">Cosmic Insight</span>
                                </div>
                                <p className="text-gray-200 italic leading-relaxed text-sm">
                                    "{insight}"
                                </p>
                            </div>
                        )}
                        <div className="mt-6 flex justify-end">
                            {!insight && (
                                cooldownStatus.locked ? (
                                    <div className="flex items-center gap-2 text-xs font-mono text-white bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 px-3 py-1.5 rounded-full shadow-lg shadow-purple-600/50 animate-in fade-in">
                                        <Clock size={12} className="animate-pulse" />
                                        <span>Next use in {cooldownStatus.text}</span>
                                    </div>
                                ) : (
                                    <button 
                                        onClick={handleExplore} 
                                        disabled={loading}
                                        className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-500 hover:to-pink-400 text-white font-semibold px-6 py-2 rounded-full transition-all shadow-lg shadow-purple-600/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 size={18} className="animate-spin" />
                                                Consulting the Stars...
                                            </>
                                        ) : (
                                            'Explore This Phase'
                                        )}
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                )}
                 <ChevronDown className={`absolute right-4 top-4 text-gray-600 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} size={20} />
            </div>
        </div>
    );
};

const TimelinePage: React.FC<TimelinePageProps> = ({ userData, settings, updateSettings }) => {
  // Set SEO meta tags for this page
  useSetMeta({
    title: 'Life Timeline & Cosmic Milestones - Astrological Life Path | ZodiAge',
    description: 'Explore your life timeline with cosmic milestones and astrological turning points. Understand your personal growth journey through the lenses of Saturn, Jupiter, and age markers.',
    ogImage: 'https://zodiage.com/logo.png'
  });
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [cooldownStatus, setCooldownStatus] = useState(() => getTimeUntilAvailable(settings.lastTimelineTimestamp));
  // Update cooldown timer every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCooldownStatus(getTimeUntilAvailable(settings.lastTimelineTimestamp));
    }, 1000);
    return () => clearInterval(interval);
  }, [settings.lastTimelineTimestamp]);
  const handleCooldownStart = () => {
    updateSettings({ lastTimelineTimestamp: Date.now() });
  };
  const hasBirthDate = !!userData.birthDate;
  
  if (!hasBirthDate || !userData.birthDate) {
    return (
        <div className="max-w-4xl mx-auto py-12 px-4 animate-in fade-in duration-700">
            <div className="flex flex-col items-center justify-center min-h-[50vh] text-center px-4">
                <div className="bg-card/30 border border-gray-800 rounded-3xl p-8 md:p-12 max-w-xl backdrop-blur-sm">
                    <div className="bg-gradient-to-r from-purple-600/20 to-pink-500/20 p-4 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                        <Milestone className="text-purple-400" size={40} />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
                        Life Milestones Timeline
                    </h1>
                    <p className="text-gray-300 text-lg mb-3">
                        Discover the major milestones that shape your journey through life.
                    </p>
                    <p className="text-gray-400 text-sm mb-8 leading-relaxed">
                        To view your personalized timeline with astrological milestones like Saturn Returns, 
                        Jupiter Returns, and more—you'll need to calculate your age first.
                    </p>
                    <div className="flex justify-center">
                        <button 
                            onClick={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }}
                            className="bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 text-white font-bold py-3.5 px-8 rounded-full hover:from-purple-500 hover:via-purple-400 hover:to-pink-400 transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-600/50 hover:shadow-purple-500/70"
                        >
                            Go to Calculator <ArrowRight size={18} />
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-6 italic">
                        Milestones are drawn from astrological traditions as a framework for reflection.
                    </p>
                </div>
            </div>
        </div>
    );
  }
  const birthYear = userData.birthDate.getFullYear();
  const currentYear = new Date().getFullYear();
  const ageDetail = calculateAge(userData.birthDate);
  const currentAge = ageDetail.years;
  const milestones = Object.entries(MILESTONE_DATA).map(([age, data]) => ({
      age: parseInt(age),
      year: birthYear + parseInt(age),
      icon: <Milestone size={16} />,
      ...data
  })).filter(m => m.year <= currentYear + 10);
  const unlockedCount = settings.tarotCollection.length;
  const totalTarot = TAROT_DECK.length;
  const handleToggle = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };
  return (

    <div className="max-w-4xl mx-auto py-12 px-4 animate-in fade-in duration-700">

                <div id="timeline-section" className="text-center mb-16">
            <h2 className="text-4xl font-display font-bold text-white mb-2">Timeline</h2>
            <p className="text-gray-400">Major life milestones and their meanings.</p>
        </div>
        <div className="mb-16 bg-card/50 border border-gray-800 rounded-2xl p-8 backdrop-blur-sm relative overflow-hidden">
             <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-2 justify-center md:justify-start">
                        <Sparkles className="text-pink-500" /> Tarot Collection
                    </h3>
                    <p className="text-gray-400 text-sm mt-1">
                        {unlockedCount} of {totalTarot} Major Arcana Discovered
                    </p>
                </div>
                
                <div className="flex flex-wrap gap-2 justify-center max-w-md">
                    {TAROT_DECK.map(card => {
                        const isUnlocked = settings.tarotCollection.includes(card.id);
                        return (
                            <div 
                                key={card.id}
                                title={isUnlocked ? card.name : "Undiscovered"}
                                className={`
                                    w-8 h-12 rounded border text-[8px] flex items-center justify-center text-center leading-none select-none transition-all
                                    ${isUnlocked 
                                        ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-pink-500 opacity-100 scale-100 hover:scale-150 z-0 hover:z-10' 
                                        : 'bg-gray-800 border-gray-700 text-gray-500'}
                                `}
                            >
                                {isUnlocked ? card.id : '?'}
                            </div>
                        );
                    })}
                </div>
             </div>
             <div className="absolute bottom-0 left-0 h-1 bg-pink-500 transition-all duration-1000" style={{ width: `${(unlockedCount/totalTarot)*100}%` }}></div>
        </div>
        <div className="relative border-l-2 border-gray-800 ml-6 md:ml-12 space-y-12">
            
            {milestones.map((milestone, idx) => {
                const isPast = milestone.year < currentYear;
                const isCurrentEra = currentAge >= milestone.age && (currentAge < (milestones[idx+1]?.age || 999));
                return (
                    <TimelineItem
                        key={milestone.age}
                        milestone={milestone}
                        isCurrentEra={isCurrentEra}
                        isPast={isPast}
                        isExpanded={expandedIndex === idx}
                        onToggle={() => handleToggle(idx)}
                        userData={userData}
                        cooldownStatus={cooldownStatus}
                        onCooldownStart={handleCooldownStart}
                    />
                );
            })}
            
            <div className="pl-8 md:pl-12 pb-12">
                 <div className="text-gray-500 text-sm italic">And the stars continue to align...</div>
            </div>
        </div>
        {/* Educational Content (AdSense Compliance) - Rendered from JSON */}
        <div className="w-full max-w-5xl mx-auto mb-12 mt-16">
            <div className="bg-cosmic-800/30 p-8 md:p-12 rounded-2xl border border-cosmic-700/50">
                <div className="seo-content" dangerouslySetInnerHTML={{ __html: timelineData.html }} />
            </div>
            {/* Multiplex Ad for Timeline Page */}
            <AdSpace type="multiplex" slot="9466105368" className="mt-12" />
        </div>
    </div>
  );
};
export default TimelinePage;
